import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../../utils/hooks';
import { Helpers } from '../../../utils/helpers';
import { IUserDto } from '../../../utils/constants/interfaces';

interface UserEnableLoginHook {
  memberId: string;
  verifiedUser: IUserDto | null;
  alertMessage: string;
  isVerifying: boolean;
  isEnabling: boolean;
  fieldErrors: string[];
  updateMemberId: (id: string) => void;
  verifyMember: () => Promise<void>;
  enableLogin: () => Promise<void>;
  clearAlert: () => void;
}

const useUserEnableLogin = (): UserEnableLoginHook => {
  const api = useApi();
  const navigate = useNavigate();

  const [memberId, setMemberId] = useState('');
  const [verifiedUser, setVerifiedUser] = useState<IUserDto | null>(null);
  const [alertMessage, setAlertMessage] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isEnabling, setIsEnabling] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);

  const updateMemberId = (id: string): void => {
    setMemberId(id);
    setAlertMessage('');
    setFieldErrors([]);
    setVerifiedUser(null);
  };

  const clearAlert = (): void => {
    setAlertMessage('');
  };

  const verifyMember = async (): Promise<void> => {
    if (isVerifying || Helpers.isNullOrWhiteSpace(memberId)) {
      return;
    }

    setIsVerifying(true);
    setAlertMessage('');
    setFieldErrors([]);
    setVerifiedUser(null);

    try {
      const endpoint = `/users/memberlegacy/${encodeURIComponent(memberId)}`;
      const apiResponse = await api.get<IUserDto>(endpoint);

      if (apiResponse.isSuccessStatusCode && apiResponse.response) {
        setVerifiedUser(apiResponse.response);
        setAlertMessage('');
      } else {
        setAlertMessage('Member not found or unable to verify member.');
      }
    } catch (error) {
      setAlertMessage('Error verifying member. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const enableLogin = async (): Promise<void> => {
    if (isEnabling || !verifiedUser) {
      return;
    }

    setIsEnabling(true);
    setAlertMessage('');

    try {
      const endpoint = `/users/${verifiedUser.id}/enable-login`;
      const apiResponse = await api.post<boolean>(endpoint, '');

      if (apiResponse.isSuccessStatusCode && apiResponse.response === true) {
        // Navigate to the user editor to set permissions
        void navigate(`/users/${verifiedUser.id}`);
      } else {
        setAlertMessage('Error enabling login for member. Please try again.');
      }
    } catch (error) {
      setAlertMessage('Error enabling login for member. Please try again.');
    } finally {
      setIsEnabling(false);
    }
  };

  return {
    memberId,
    verifiedUser,
    alertMessage,
    isVerifying,
    isEnabling,
    fieldErrors,
    updateMemberId,
    verifyMember,
    enableLogin,
    clearAlert
  };
};

export default useUserEnableLogin;
